import { useEffect, useRef, useState } from "react";
import { Terminal } from "xterm";
import { FitAddon } from "xterm-addon-fit";
import "xterm/css/xterm.css";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";

const RESET = "\x1b[0m";
const DIM = "\x1b[90m";
const CYAN = "\x1b[36m";
const GREEN = "\x1b[32m";
const YELLOW = "\x1b[33m";
const RED = "\x1b[31m";
const BOLD = "\x1b[1m";

const PROMPT = `${GREEN}you${RESET} ${DIM}❯${RESET} `;

function safeWrite(term, line) {
  if (!term) return;
  try { term.write(line); } catch {/* xterm renderer not ready */}
}
function safeWriteln(term, line) {
  if (!term) return;
  try { term.writeln(line); } catch {/* xterm renderer not ready */}
}
function safeFit(fit, container) {
  if (!fit || !container || !container.isConnected) return;
  const w = container.clientWidth || 0;
  const h = container.clientHeight || 0;
  if (w < 20 || h < 20) return;
  try { fit.fit(); } catch {/* noop */}
}

const TXT = {
  pt: {
    boot: "// ratchet socrático · esclarecimento via conversa",
    waiting: "iniciando sessão...",
    skipped: "[sessão pulada — clique em 'Investigar' para análise direta]",
    finalized: "[spec finalizada · pronto para investigação automatizada]",
    typing: "ratchet pensando...",
    wsLive: "[ws] conectado · live",
    wsDown: "[ws] desconectado — tentando reconectar em 3s...",
  },
  en: {
    boot: "// ratchet socratic · clarification via conversation",
    waiting: "starting session...",
    skipped: "[session skipped — click 'Investigate' for direct analysis]",
    finalized: "[spec finalized · ready for automated investigation]",
    typing: "ratchet thinking...",
    wsLive: "[ws] connected · live",
    wsDown: "[ws] disconnected — reconnecting in 3s...",
  },
  es: {
    boot: "// ratchet socrático · aclaración mediante conversación",
    waiting: "iniciando sesión...",
    skipped: "[sesión omitida — haz clic en 'Investigar' para análisis directo]",
    finalized: "[spec finalizada · lista para investigación automatizada]",
    typing: "ratchet pensando...",
    wsLive: "[ws] conectado · live",
    wsDown: "[ws] desconectado — reconectando en 3s...",
  },
};

/**
 * Interactive socratic REPL for Ratchet (bidirectional WS over xterm).
 * Props: cardId, apiBase, token, onFinalize({spec_yaml, suggested_response})
 */
export default function RatchetREPL({ cardId, apiBase, token, onFinalize, onSkip }) {
  const { lang } = useLang();
  const txt = TXT[lang] || TXT.pt;
  const containerRef = useRef(null);
  const termRef = useRef(null);
  const fitRef = useRef(null);
  const wsRef = useRef(null);
  const inputRef = useRef("");
  const sentTurnIdsRef = useRef(new Set()); // dedup turns echoed back from WS
  const pendingRef = useRef([]);
  const openedRef = useRef(false);
  const [connected, setConnected] = useState(false);
  const [finalized, setFinalized] = useState(false);

  // ----- xterm init -----
  useEffect(() => {
    if (!containerRef.current || termRef.current) return;
    let cancelled = false;
    let resizeObs = null;

    const raf1 = requestAnimationFrame(() => requestAnimationFrame(() => {
      if (cancelled || !containerRef.current) return;
      const term = new Terminal({
        convertEol: true,
        fontFamily: "'JetBrains Mono', 'Fira Code', ui-monospace, monospace",
        fontSize: 12,
        lineHeight: 1.4,
        theme: {
          background: "#0a0a0a",
          foreground: "#e5e5e5",
          cursor: "#22c55e",
          selectionBackground: "#3b3b3b",
        },
        cursorBlink: true,
        scrollback: 2000,
      });
      const fit = new FitAddon();
      term.loadAddon(fit);
      term.open(containerRef.current);
      termRef.current = term;
      fitRef.current = fit;
      openedRef.current = true;
      safeFit(fit, containerRef.current);

      safeWriteln(term, `${DIM}${txt.boot}${RESET}`);
      safeWrite(term, PROMPT);

      // Handle user input
      term.onData((data) => {
        if (finalized) return;
        for (const ch of data) {
          const code = ch.charCodeAt(0);
          if (code === 13) {
            // Enter — submit
            const msg = inputRef.current.trim();
            safeWrite(term, "\r\n");
            if (msg && wsRef.current?.readyState === 1) {
              wsRef.current.send(JSON.stringify({ type: "send", text: msg }));
              safeWriteln(term, `${DIM}${txt.typing}${RESET}`);
            }
            inputRef.current = "";
            if (!msg) safeWrite(term, PROMPT);
          } else if (code === 127 || code === 8) {
            // Backspace
            if (inputRef.current.length > 0) {
              inputRef.current = inputRef.current.slice(0, -1);
              safeWrite(term, "\b \b");
            }
          } else if (code >= 32 && code < 127) {
            inputRef.current += ch;
            safeWrite(term, ch);
          }
        }
      });

      // Flush buffered messages that arrived before xterm.open() finished
      const flush = () => {
        const q = pendingRef.current;
        pendingRef.current = [];
        for (const line of q) safeWriteln(term, line);
        safeWrite(term, PROMPT);
      };
      requestAnimationFrame(() => requestAnimationFrame(flush));

      try {
        resizeObs = new ResizeObserver(() => safeFit(fit, containerRef.current));
        resizeObs.observe(containerRef.current);
      } catch {/* noop */}
    }));

    const onResize = () => safeFit(fitRef.current, containerRef.current);
    window.addEventListener("resize", onResize);

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf1);
      window.removeEventListener("resize", onResize);
      try { resizeObs?.disconnect(); } catch {/* noop */}
      try { termRef.current?.dispose(); } catch {/* noop */}
      termRef.current = null;
      fitRef.current = null;
      openedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ----- WS lifecycle -----
  useEffect(() => {
    if (!cardId || !apiBase || !token) return;
    let closed = false;
    let reconnectTimer = null;

    const writeOrQueue = (line) => {
      if (termRef.current && openedRef.current) safeWriteln(termRef.current, line);
      else pendingRef.current.push(line);
    };

    // Auto-start session before opening WS (idempotent: backend returns started:true or resumed:true)
    (async () => {
      try {
        await api.post(`/cards/${cardId}/ratchet/start`, { lang });
      } catch {/* ignore */}
    })();

    const buildWsUrl = () => {
      const wsBase = apiBase.replace(/^http/, "ws").replace(/\/+$/, "");
      return `${wsBase}/api/ws/cards/${cardId}/ratchet?token=${encodeURIComponent(token)}`;
    };

    const connect = () => {
      try {
        const ws = new WebSocket(buildWsUrl());
        wsRef.current = ws;
        ws.onopen = () => {
          setConnected(true);
          writeOrQueue(`${DIM}${txt.wsLive}${RESET}`);
        };
        ws.onmessage = (ev) => {
          try {
            const data = JSON.parse(ev.data);
            if (data.event === "PONG") return;
            if (data.event === "turn") {
              // Dedup by at+role
              const key = `${data.at}:${data.role}`;
              if (sentTurnIdsRef.current.has(key)) return;
              sentTurnIdsRef.current.add(key);
              const term = termRef.current;
              if (data.role === "user") {
                writeOrQueue(`${GREEN}you${RESET} ${DIM}❯${RESET} ${data.text}`);
              } else {
                writeOrQueue(`${CYAN}${BOLD}ratchet${RESET} ${DIM}❯${RESET} ${data.text}`);
                if (term) safeWrite(term, PROMPT);
              }
            } else if (data.event === "finalized") {
              setFinalized(true);
              writeOrQueue(`${YELLOW}${txt.finalized}${RESET}`);
              if (onFinalize) onFinalize({
                spec_yaml: data.spec_yaml || "",
                suggested_response: data.suggested_response || "",
              });
            } else if (data.event === "skipped") {
              writeOrQueue(`${YELLOW}${txt.skipped}${RESET}`);
            } else if (data.event === "error") {
              writeOrQueue(`${RED}error: ${data.text}${RESET}`);
            }
          } catch {/* ignore non-JSON */}
        };
        ws.onclose = () => {
          setConnected(false);
          if (closed) return;
          writeOrQueue(`${DIM}${txt.wsDown}${RESET}`);
          reconnectTimer = setTimeout(connect, 3000);
        };
        ws.onerror = () => {/* close follows */};
      } catch (e) {
        writeOrQueue(`${RED}[ws] failed: ${e.message}${RESET}`);
      }
    };
    connect();

    return () => {
      closed = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      try { wsRef.current?.close(); } catch {/* noop */}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardId, apiBase, token, lang]);

  const handleSkip = async () => {
    try {
      await api.post(`/cards/${cardId}/ratchet/skip`);
      setFinalized(true);
      if (onSkip) onSkip();
    } catch {/* ignore */}
  };

  const handleForceFinalize = async () => {
    try {
      await api.post(`/cards/${cardId}/ratchet/finalize`);
    } catch {/* ignore */}
  };

  return (
    <div className="surface p-0 overflow-hidden" data-testid="ratchet-repl">
      <div className="flex items-center justify-between gap-3 px-4 py-2 border-b border-white/10 bg-black/40">
        <div className="flex items-center gap-2 text-xs font-mono text-white/70">
          <span className="inline-block w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
          <span>ratchet socrático · {cardId?.slice(0, 8) || "—"}</span>
        </div>
        <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-wider">
          <span className={connected ? "text-emerald-400" : "text-amber-400"} data-testid="ratchet-ws-status">
            {connected ? "live" : "offline"}
          </span>
          <button
            onClick={handleForceFinalize}
            disabled={finalized}
            className="text-[10px] uppercase tracking-wider text-white/50 hover:text-white px-2 py-0.5 border border-white/15 disabled:opacity-30"
            data-testid="ratchet-finalize-btn"
          >{lang === "pt" ? "Encerrar e gerar spec" : lang === "es" ? "Cerrar y generar spec" : "Finalize & emit spec"}</button>
          <button
            onClick={handleSkip}
            disabled={finalized}
            className="text-[10px] uppercase tracking-wider text-white/50 hover:text-white px-2 py-0.5 border border-white/15 disabled:opacity-30"
            data-testid="ratchet-skip-btn"
          >{lang === "pt" ? "Pular" : lang === "es" ? "Omitir" : "Skip"}</button>
        </div>
      </div>
      <div ref={containerRef} className="h-80 bg-[#0a0a0a]" data-testid="ratchet-pane" />
    </div>
  );
}
